package com.videoanalyzer.web.components.analysis

import androidx.compose.runtime.Composable
import com.videoanalyzer.web.config.Config
import com.videoanalyzer.web.models.FullAnalysis
import com.videoanalyzer.web.state.getScoreLabel
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Score display components for analysis results.
 */

@Composable
fun OverallScoreCard(score: Int) {
    val scoreColor = getScoreColor(score)
    val scoreLabel = getScoreLabel(score)
    val scoreGlowClass = when {
        score >= 80 -> "score-glow-excellent"
        score >= 60 -> "score-glow-good"
        score >= 40 -> "score-glow-average"
        else -> "score-glow-poor"
    }

    Div({
        classes("card", "scale-in")
        style {
            textAlign("center")
            padding(40.px)
        }
    }) {
        P({
            style {
                color(Color("#A1A1AA"))
                fontSize(12.px)
                marginBottom(16.px)
                fontWeight(500)
                letterSpacing(1.px)
                property("text-transform", "uppercase")
            }
        }) { Text("Overall Performance Score") }

        // Score display - with glow effect
        Div({
            classes(scoreGlowClass)
            style {
                fontFamily("'SF Mono', Monaco, monospace")
                fontSize(72.px)
                fontWeight(700)
                lineHeight("1")
                color(Color(scoreColor))
            }
        }) { Text("$score") }

        // Score label badge
        Div({
            style {
                marginTop(16.px)
                display(DisplayStyle.InlineBlock)
                backgroundColor(Color(scoreColor))
                color(Color.white)
                padding(8.px, 20.px)
                borderRadius(50.px)
                fontSize(12.px)
                fontWeight(600)
                letterSpacing(0.5.px)
            }
        }) { Text(scoreLabel) }

        // Score breakdown indicator
        Div({
            style {
                marginTop(24.px)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                gap(32.px)
            }
        }) {
            ScoreIndicator("Title", (score * 0.9).toInt(), "#0EA5E9")
            ScoreIndicator("Thumbnail", (score * 1.1).toInt().coerceAtMost(100), "#22C55E")
        }
    }
}

@Composable
fun ScoreIndicator(label: String, score: Int, color: String) {
    Div({ style { textAlign("center") } }) {
        Div({
            style {
                fontSize(28.px)
                fontWeight(700)
                color(Color(color))
                fontFamily("'SF Mono', Monaco, monospace")
            }
        }) { Text("$score") }
        Div({
            style {
                fontSize(12.px)
                color(Color("#71717A"))
                marginTop(4.px)
                fontWeight(500)
            }
        }) { Text(label) }
    }
}

@Composable
fun ScoreSummaryCard(
    icon: String,
    label: String,
    score: Int,
    gradientStart: String,
    gradientEnd: String
) {
    val scoreColor = getScoreColor(score)

    Div({
        classes("card")
        style {
            textAlign("center")
            padding(20.px)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                gap(8.px)
                marginBottom(12.px)
            }
        }) {
            Div({
                style {
                    width(32.px)
                    height(32.px)
                    borderRadius(6.px)
                    backgroundColor(Color("#27272A"))
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    fontSize(16.px)
                }
            }) { Text(icon) }

            Span({
                style {
                    fontSize(13.px)
                    fontWeight(500)
                    color(Color("#A1A1AA"))
                }
            }) { Text(label) }
        }

        Div({
            style {
                fontFamily("'SF Mono', Monaco, monospace")
                fontSize(40.px)
                fontWeight(700)
                color(Color(scoreColor))
                lineHeight("1")
            }
        }) { Text("$score") }

        Div({
            style {
                marginTop(8.px)
                display(DisplayStyle.InlineBlock)
                backgroundColor(Color("${scoreColor}20"))
                color(Color(scoreColor))
                padding(4.px, 10.px)
                borderRadius(4.px)
                fontSize(11.px)
                fontWeight(600)
            }
        }) { Text(getScoreLabel(score)) }
    }
}

@Composable
fun FeatureTag(text: String) {
    Div({
        style {
            backgroundColor(Color("#27272A"))
            color(Color("#A1A1AA"))
            padding(6.px, 12.px)
            borderRadius(4.px)
            fontSize(12.px)
            fontWeight(500)
        }
    }) { Text(text) }
}

/**
 * Gated view for non-first analyses (free tier).
 * Shows scores only with upgrade CTA to unlock detailed analysis.
 */
@Composable
fun GatedAnalysisView(
    analysis: FullAnalysis,
    onUpgrade: () -> Unit
) {
    val titleScore = analysis.titleAnalysis.overallScore * 10
    val thumbnailScore = analysis.thumbnailAnalysis?.overallScore ?: 0

    // Score summary cards
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(auto-fit, minmax(200px, 1fr))")
            gap(16.px)
            marginTop(24.px)
        }
    }) {
        ScoreSummaryCard(
            icon = "📝",
            label = "Title Score",
            score = titleScore,
            gradientStart = "rgba(99, 102, 241, 0.2)",
            gradientEnd = "rgba(139, 92, 246, 0.2)"
        )

        ScoreSummaryCard(
            icon = "🖼️",
            label = "Thumbnail Score",
            score = thumbnailScore,
            gradientStart = "rgba(16, 185, 129, 0.2)",
            gradientEnd = "rgba(5, 150, 105, 0.2)"
        )
    }

    // Blurred preview with upgrade CTA
    Div({
        style {
            marginTop(24.px)
            position(Position.Relative)
            borderRadius(16.px)
            overflow("hidden")
        }
    }) {
        // Blurred background showing analysis cards
        Div({
            style {
                property("filter", "blur(8px)")
                opacity(0.4)
                property("pointer-events", "none")
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "repeat(auto-fit, minmax(300px, 1fr))")
                    gap(16.px)
                    padding(16.px)
                }
            }) {
                repeat(4) {
                    Div({
                        classes("card")
                        style {
                            height(180.px)
                            backgroundColor(Color("rgba(30, 41, 59, 0.8)"))
                        }
                    }) {
                        Div({ style { padding(20.px) } }) {
                            Div({
                                style {
                                    width(120.px)
                                    height(16.px)
                                    backgroundColor(Color("rgba(100, 116, 139, 0.5)"))
                                    borderRadius(4.px)
                                    marginBottom(16.px)
                                }
                            })
                            repeat(3) {
                                Div({
                                    style {
                                        width(100.percent)
                                        height(12.px)
                                        backgroundColor(Color("rgba(100, 116, 139, 0.3)"))
                                        borderRadius(4.px)
                                        marginBottom(8.px)
                                    }
                                })
                            }
                        }
                    }
                }
            }
        }

        // Upgrade CTA overlay
        Div({
            style {
                position(Position.Absolute)
                top(0.px)
                left(0.px)
                right(0.px)
                bottom(0.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                backgroundColor(Color("#18181B"))
                padding(32.px)
                textAlign("center")
            }
        }) {
            Div({
                style {
                    width(48.px)
                    height(48.px)
                    borderRadius(8.px)
                    backgroundColor(Color("#27272A"))
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    marginBottom(16.px)
                    fontSize(24.px)
                }
            }) { Text("🔒") }

            H3({
                style {
                    fontSize(18.px)
                    fontWeight(600)
                    color(Color("#FAFAFA"))
                    marginBottom(8.px)
                }
            }) { Text("Unlock Full Analysis") }

            P({
                style {
                    color(Color("#A1A1AA"))
                    fontSize(14.px)
                    lineHeight("1.5")
                    marginBottom(8.px)
                    maxWidth(360.px)
                }
            }) { Text("Your first analysis showed everything. Now see what Pro unlocks:") }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexWrap(FlexWrap.Wrap)
                    justifyContent(JustifyContent.Center)
                    gap(8.px)
                    marginTop(12.px)
                    marginBottom(20.px)
                }
            }) {
                FeatureTag("📝 Title Optimization Tips")
                FeatureTag("🖼️ Thumbnail Improvements")
                FeatureTag("📊 15+ Analysis Categories")
                FeatureTag("⚡ Actionable Recommendations")
            }

            Button({
                classes("btn", "btn-primary")
                style {
                    padding(12.px, 32.px)
                    fontSize(14.px)
                    fontWeight(600)
                    cursor("pointer")
                }
                onClick { onUpgrade() }
            }) {
                Span({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(8.px)
                    }
                }) {
                    Text("Upgrade to Pro")
                    Span({ style { fontSize(14.px) } }) { Text("→") }
                }
            }

            P({
                style {
                    color(Color("#71717A"))
                    fontSize(12.px)
                    marginTop(10.px)
                }
            }) { Text("Starting from ${Config.CURRENCY_SYMBOL}${Config.STARTING_PRICE}") }
        }
    }
}
